// Apis
import SupportApi from '@/api/general/support-api';

// Constants
import { OPTIONS_STORE_SUPPORT_WIDGET } from '@/vuex/module/supportWidget';
import { SUPPORT_ENTRY_PATH } from '@/constants/route-constants';

// Services
import logService from '@/services/logService';
import i18n from '@/plugins/i18n';

// Utils
import { isAdministrativeProfile } from '@/utils/profile';
import {
  createWidgetBridge,
  readDenial,
  readLauncher,
  writeLauncher,
  WIDGET_STATUS
} from '@/utils/support-widget-bridge';
import {
  ASSERTION_FAILURE,
  FRESH_ASSERTION_MS,
  assertionFailure,
  forgetSession,
  readKnownSession,
  rememberSession,
  requestAssertion
} from '@/utils/support-widget-session';

/* Depois do logout o iframe ainda precisa de um instante para revogar a sessao
   do suporte: so entao ele sai da pagina. */
const LOGOUT_GRACE_MS = 400;
const NEW_TAB = '_blank';
const NEW_TAB_FEATURES = 'noopener';

/**
 * Sessao do chat de suporte embutido: quem pode ter, de onde sai o iframe e a
 * ponte que conversa com ele. O componente que usa este mixin so desenha.
 *
 * Espera do componente: `appData` injetado, `$refs.frame` (o iframe) e
 * `sendMessage` (mixin-message).
 */
const mixinSupportWidgetSession = {
  data() {
    return {
      supportApi: null,
      /* { id, portalOrigin, userKey }: existe enquanto o iframe esta na pagina. */
      session: null,
      sessionSeq: 0
    };
  },

  created() {
    /* Fora do `data()` de proposito: nada disto e desenhado, e a ponte nao pode
       virar objeto reativo. A primeira sessao comeca aqui, e nao num watch
       imediato, porque ele roda antes deste hook e teria a ponte apagada. */
    this.bridge = null;
    this.freshAssertion = null;
    this.logoutTimer = null;
    this.restartSession(this.supportUserId);
  },

  computed: {
    /* So o perfil Administrative tem suporte: a mesma regra do item do menu e
       da emissao da assercao no servidor. */
    supportUserId() {
      const user = this.appData?.currentUser;

      return isAdministrativeProfile(user) && user?.id ? user.id : null;
    }
  },

  watch: {
    supportUserId: 'restartSession'
  },

  beforeDestroy() {
    this.detachBridge();
    clearTimeout(this.logoutTimer);
  },

  methods: {
    async restartSession(userId) {
      this.endSession();

      if (userId) await this.startSession(userId);
    },

    async startSession(userId) {
      this.supportApi = new SupportApi(this.appData.currentUser);

      const known = readKnownSession(userId) || (await this.discoverSession(userId));
      const isStillCurrent = this.supportUserId === userId;

      if (!known || !isStillCurrent || readDenial(known.userKey)) return;

      clearTimeout(this.logoutTimer);
      this.sessionSeq += 1;
      this.session = { id: this.sessionSeq, portalOrigin: known.portalOrigin, userKey: known.userKey };
      this.attachBridge();
    },

    /* Primeira vez na aba: a assercao diz onde fica o portal e quem e o usuario
       para o DeepDesk. O token dela fica guardado por instantes, para o iframe
       nao precisar de uma segunda chamada logo em seguida. */
    async discoverSession(userId) {
      try {
        const found = await requestAssertion(this.supportApi);

        if (!found) return null;

        rememberSession(userId, found);
        this.freshAssertion = { token: found.token, issuedAt: Date.now() };

        return found;
      } catch (error) {
        logService.warn('Chat de suporte indisponível: não foi possível obter a asserção', error);

        return null;
      }
    },

    attachBridge() {
      this.bridge = createWidgetBridge({
        portalOrigin: this.session.portalOrigin,
        userKey: this.session.userKey,
        frame: () => this.$refs.frame?.contentWindow || null,
        requestAssertion: this.provideAssertion,
        onChange: this.onBridgeChange,
        onAlert: this.onSupportAlert,
        onOpenPortal: this.openFullPortal
      });

      const snapshot = this.bridge.snapshot();

      this.$store.commit(OPTIONS_STORE_SUPPORT_WIDGET.MUTATIONS.SET_SNAPSHOT, snapshot);
      /* Recarregou com o chat em uso (bolinha na tela ou painel aberto): volta igual. */
      this.$store.commit(
        OPTIONS_STORE_SUPPORT_WIDGET.MUTATIONS.SET_SUMMONED,
        readLauncher(this.session.userKey) || snapshot.open
      );
      window.addEventListener('message', this.bridge.handleMessage);
    },

    detachBridge() {
      if (!this.bridge) return;

      window.removeEventListener('message', this.bridge.handleMessage);
      this.bridge = null;
      this.freshAssertion = null;
    },

    /* A pessoa saiu do sistema ou perdeu o direito: o iframe revoga a sessao do
       suporte e so depois sai da pagina. */
    endSession() {
      if (!this.bridge) return;

      const retiringId = this.session.id;

      this.bridge.logout();
      this.detachBridge();
      forgetSession();
      this.$store.commit(OPTIONS_STORE_SUPPORT_WIDGET.MUTATIONS.RESET);
      this.logoutTimer = setTimeout(() => this.dropSession(retiringId), LOGOUT_GRACE_MS);
    },

    dropSession(sessionId) {
      if (this.session?.id === sessionId) this.session = null;
    },

    onBridgeChange(snapshot) {
      this.$store.commit(OPTIONS_STORE_SUPPORT_WIDGET.MUTATIONS.SET_SNAPSHOT, snapshot);

      if (snapshot.status !== WIDGET_STATUS.DENIED) return;

      /* Recusado: o iframe sai da pagina. A ponte ja guardou o motivo e por
         quanto tempo; ate la o menu "Suporte" abre o portal em outra aba. */
      this.detachBridge();
      this.session = null;
    },

    async provideAssertion() {
      const fresh = this.takeFreshAssertion();

      if (fresh) return { token: fresh };

      try {
        const found = await requestAssertion(this.supportApi);

        return found ? { token: found.token } : { reason: ASSERTION_FAILURE.UNAVAILABLE };
      } catch (error) {
        return { reason: assertionFailure(error) };
      }
    },

    takeFreshAssertion() {
      const stored = this.freshAssertion;

      this.freshAssertion = null;

      return stored && Date.now() - stored.issuedAt < FRESH_ASSERTION_MS ? stored.token : null;
    },

    /* Som e aviso sao do produto: dentro do iframe o navegador nao deixa. */
    onSupportAlert(alert) {
      this.sendMessage(i18n.t('comum.supportWidgetSessionUi.replyAlert', { author: alert.authorName, preview: alert.preview }), 'info');
      this.summonLauncher(true);
    },

    /* "Abrir o portal completo": o mesmo /suporte do menu, na tela em que a
       pessoa estava dentro do painel. */
    openFullPortal(path) {
      const target = this.$router.resolve({ path: SUPPORT_ENTRY_PATH, query: { destino: path } });

      window.open(target.href, NEW_TAB, NEW_TAB_FEATURES);
    },

    summonLauncher(isShown) {
      if (!this.session) return;

      writeLauncher(this.session.userKey, isShown);
      this.$store.commit(OPTIONS_STORE_SUPPORT_WIDGET.MUTATIONS.SET_SUMMONED, isShown);
    }
  }
};

export default mixinSupportWidgetSession;
